import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ShieldCheck, Lock, Upload, ChevronLeft, ChevronRight, CheckCircle2, FileText, Loader2 } from "lucide-react";
import { FORM_PAGES, DOC_TYPES, DECLARATIONS } from "../lib/claimFormPages";
import { ClaimStatusTracker } from "../components/ClaimStatusTracker";
import { ClaimEvidenceCall } from "../components/ClaimEvidenceCall";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const GOLD = "#C9A84C";

function Field({ page, f, value, onChange, idx }) {
  const id = `f-${page}-${f.key}`;
  const base = "w-full border border-[#2a2a2a] bg-[#faf8f2] px-3 py-2.5 text-[15px] text-[#111] outline-none focus:border-[#C9A84C] focus:ring-1 focus:ring-[#C9A84C]";
  const label = (
    <label htmlFor={id} className="block text-[13px] font-semibold uppercase tracking-wide text-[#333]">
      <span className="inline-block w-9 text-[#8a6d1f] font-mono">{page}.{idx + 1}</span>{f.label}{f.required && <span className="text-[#a3261f]"> *</span>}
    </label>
  );
  if (f.type === "yesno") {
    return (
      <div className="py-3 border-b border-dotted border-[#bbb]" data-testid={id}>
        {label}
        <div className="mt-2 flex gap-6 pl-9">
          {["Yes", "No"].map((o) => (
            <label key={o} className="inline-flex items-center gap-2 text-[15px] text-[#111] cursor-pointer">
              <input type="radio" name={id} data-testid={`${id}-${o.toLowerCase()}`} checked={value === o} onChange={() => onChange(o)} className="accent-[#8a6d1f] w-4 h-4" /> {o}
            </label>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="py-3 border-b border-dotted border-[#bbb]">
      {label}
      <div className="mt-2 pl-9">
        {f.type === "select" ? (
          <select id={id} data-testid={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base}>
            <option value="">— select —</option>{f.options.map((o) => <option key={o}>{o}</option>)}
          </select>
        ) : f.type === "textarea" ? (
          <textarea id={id} data-testid={id} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={base} />
        ) : (
          <input id={id} data-testid={id} type={f.type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={base} />
        )}
      </div>
    </div>
  );
}

function Watermark() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden select-none" aria-hidden>
      {[0, 1, 2].map((i) => (
        <div key={i} className="absolute left-1/2 font-serif font-bold uppercase tracking-[0.35em] text-[#8a6d1f]/[0.07] whitespace-nowrap"
             style={{ top: `${18 + i * 30}%`, transform: "translateX(-50%) rotate(-28deg)", fontSize: "88px" }}>Vinci Inc · Official</div>
      ))}
    </div>
  );
}

export default function ClaimForm() {
  const { token } = useParams();
  const storeKey = `vinci.claimform.${token}`;
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [answers, setAnswers] = useState(() => { try { return JSON.parse(localStorage.getItem(storeKey)) || {}; } catch { return {}; } });
  const [decl, setDecl] = useState({});
  const [session, setSession] = useState(() => sessionStorage.getItem(`${storeKey}.session`) || "");
  const [otpSent, setOtpSent] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/claim-form/${token}`).then((r) => {
      setMeta(r.data);
      if (r.data.form_status === "submitted") setDone({ status: "customer_submitted", submitted_at: r.data.submitted_at, claim_ref: r.data.claim_ref });
    }).catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  }, [token]);
  useEffect(() => { localStorage.setItem(storeKey, JSON.stringify(answers)); }, [answers, storeKey]);

  const cur = FORM_PAGES[page - 1];
  const pageAnswers = answers[`page_${page}`] || {};
  const setA = (key, v) => setAnswers((a) => ({ ...a, [`page_${page}`]: { ...(a[`page_${page}`] || {}), [key]: v } }));
  const prefilled = useCallback((f) => {
    if (!f.prefill || !meta) return undefined;
    const v = meta[f.prefill];
    if (f.prefill === "business_name" && !v) return meta.buyer_email;
    return v;
  }, [meta]);
  useEffect(() => {
    if (!meta || !cur) return;
    const fill = {};
    cur.fields.forEach((f) => { const v = prefilled(f); if (v !== undefined && v !== null && pageAnswers[f.key] === undefined) fill[f.key] = v; });
    if (Object.keys(fill).length) setAnswers((a) => ({ ...a, [`page_${page}`]: { ...fill, ...(a[`page_${page}`] || {}) } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, meta]);

  const missing = useMemo(() => cur ? cur.fields.filter((f) => f.required && !pageAnswers[f.key]).map((f) => f.label) : [], [cur, pageAnswers]);
  const docs = meta?.documents || [];
  const docsMissing = DOC_TYPES.filter((d) => d.required && !docs.some((x) => x.doc_type === d.key));
  const authHeaders = { headers: { "X-Claim-Session": session } };

  const next = () => {
    if (missing.length) { toast.error(`Complete required fields: ${missing.slice(0, 3).join(", ")}${missing.length > 3 ? "…" : ""}`); return; }
    if (cur.upload && docsMissing.length) { toast.error(`Upload mandatory documents: ${docsMissing.map((d) => d.label).join(", ")}`); return; }
    setPage((p) => Math.min(11, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const startOtp = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/claim-form/${token}/otp/start`); setOtpSent(r.data); toast.success(`OTP sent to ${r.data.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail?.reason || e.response?.data?.detail || "Could not send OTP"); }
    finally { setBusy(false); }
  };
  const verifyOtp = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/claim-form/${token}/otp/verify`, { code: otp }); setSession(r.data.session); sessionStorage.setItem(`${storeKey}.session`, r.data.session); toast.success("Identity verified — form unlocked"); }
    catch (e) { toast.error(e.response?.data?.detail?.code === "invalid" ? `Incorrect code · ${e.response.data.detail.attempts_left} attempts left` : (e.response?.data?.detail?.code || "Verification failed")); }
    finally { setBusy(false); }
  };
  const upload = async (docType, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
    setBusy(true);
    try { const r = await axios.post(`${API}/claim-form/${token}/upload`, fd, authHeaders); setMeta((m) => ({ ...m, documents: [...(m.documents || []), r.data.document] })); toast.success(`${file.name} uploaded`); }
    catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    const p11 = answers.page_11 || {};
    if (missing.length) { toast.error("Complete the signature block first"); return; }
    if (DECLARATIONS.some((d) => !decl[d.key])) { toast.error("Tick every final declaration"); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/claim-form/${token}/submit`, { answers, signature_name: p11.signature_name, signature_designation: p11.signature_designation, place: p11.place, declarations: decl }, authHeaders);
      setDone(r.data); localStorage.removeItem(storeKey); sessionStorage.removeItem(`${storeKey}.session`);
    } catch (e) { const d = e.response?.data?.detail; toast.error(d?.code === "pages_incomplete" ? `Only ${d.pages_done} of ${d.required} pages completed` : d?.code === "evidence_missing" ? d.message : d?.message || d?.code || d || "Submission failed"); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen bg-[#0D0D0D] text-[#F5F5F5] grid place-items-center p-8"><div className="max-w-md text-center" data-testid="claim-form-error"><Lock className="mx-auto text-[#C9A84C]" /><h1 className="font-serif text-2xl mt-4">Link unavailable</h1><p className="text-sm text-[#888888] mt-2">{String(err)}</p></div></div>;
  if (!meta) return <div className="min-h-screen bg-[#0D0D0D] grid place-items-center"><Loader2 className="animate-spin text-[#C9A84C]" /></div>;

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-[#111]" data-testid="claim-form-page">
      <Toaster position="top-center" richColors />
      <header className="bg-[#0D0D0D] text-[#F5F5F5] border-b-4" style={{ borderColor: GOLD }}>
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <img src="/logo-mark.png" alt="Vinci" className="h-9 w-auto" />
            <div>
              <div className="font-serif text-2xl">Vinci Inc. · Trade Desk</div>
              <div className="text-sm tracking-[0.25em] uppercase" style={{ color: GOLD }}>Commission Claim Form · Form VNC/ICC-11</div>
            </div>
          </div>
          <div className="text-sm text-right text-[#D4D4D4]">
            <div>Claim Ref <span className="font-mono text-[#F5F5F5]" data-testid="claim-ref">{meta.claim_ref}</span></div>
            <div>Order {meta.order_ref} · Link valid till {String(meta.expires_at).slice(0, 10)}</div>
            <div className="mt-1 inline-flex items-center gap-1" style={{ color: session ? "#2FB999" : GOLD }} data-testid="identity-status">{session ? <><ShieldCheck size={14} /> Identity verified</> : <><Lock size={14} /> Identity not yet verified</>}</div>
          </div>
        </div>
      </header>

      {done && meta.evidence_call?.status === "open" ? (
        <ClaimEvidenceCall token={token} meta={meta} setMeta={setMeta}
          onFurnished={(ec) => { setMeta((m) => ({ ...m, evidence_call: ec })); toast.success("Evidence furnished — the Trade Desk has been notified"); }} />
      ) : done ? (
        <main className="max-w-3xl mx-auto px-6 py-16 text-center text-[#F5F5F5]" data-testid="claim-form-done">
          <CheckCircle2 size={56} className="mx-auto" style={{ color: GOLD }} />
          <h1 className="font-serif text-3xl mt-6">Claim lodged under seal</h1>
          <p className="text-base text-[#D4D4D4] mt-3">Form VNC/ICC-11 for Claim {done.claim_ref} has been entered in the Vinci ledger as <b>CUSTOMER SUBMITTED</b>. The dossier, together with every supporting document furnished, now stands referred to the Vinci Trade Desk for scrutiny. A formal acknowledgement has been issued to your registered email.</p>
          <p className="text-sm text-[#888888] mt-2">Lodged {String(done.submitted_at).slice(0, 16).replace("T", " ")} UTC · No further amendment is permitted without a Trade Desk re-registration order.</p>
          <ClaimStatusTracker token={token} />
        </main>
      ) : (
        <main className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex flex-wrap gap-1 mb-6" data-testid="claim-form-stepper">
            {FORM_PAGES.map((p) => (
              <button key={p.n} data-testid={`step-${p.n}`} onClick={() => p.n < page && setPage(p.n)} className={`px-3 py-1.5 text-sm font-mono border ${p.n === page ? "bg-[#C9A84C] text-black border-[#C9A84C]" : p.n < page ? "bg-[#2a2a2a] text-[#F5F5F5] border-[#444]" : "bg-transparent text-[#777] border-[#333]"}`}>
                {String(p.n).padStart(2, "0")}
              </button>
            ))}
          </nav>
          <section className="relative bg-[#f3efe4] shadow-2xl border border-[#c9bfa3] px-6 md:px-14 py-10" data-testid={`claim-page-${page}`}>
            <Watermark />
            <div className="relative">
              <div className="flex items-start justify-between gap-4 border-b-2 border-[#111] pb-4">
                <div>
                  <div className="text-[12px] font-mono uppercase tracking-[0.3em] text-[#8a6d1f]">Part {page} of 11 · For official use of Vinci Inc.</div>
                  <h2 className="font-serif text-2xl md:text-3xl mt-1">{cur.title}</h2>
                  <p className="text-[14px] text-[#444] mt-2 max-w-2xl">{cur.intro}</p>
                </div>
                <div className="text-right text-[12px] font-mono text-[#555] shrink-0">
                  <div>FORM VNC/ICC-11</div><div>REV. 2026-10</div><div>REF {meta.claim_ref}</div>
                </div>
              </div>

              {page === 1 && (
                <div className="mt-6 border-2 border-[#111] bg-[#fff] p-5 text-[13px] leading-relaxed" data-testid="claimant-notice">
                  <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#a3261f]">Notice to claimant · read before proceeding</div>
                  <ol className="list-decimal pl-5 mt-2 space-y-1 text-[#222]">
                    <li>This is a formal claim under the Vinci Indirect Commission Protocol. Every particular furnished is treated as a sworn statement of record.</li>
                    <li>The claim is <b>evidence-backed</b>: the Purchase Order, tax invoice(s) and delivery proof are mandatory and are verified for authenticity and reconciled line-by-line.</li>
                    <li>The dossier undergoes multi-tier Trade Desk scrutiny — identity, document authenticity, PO–invoice–delivery reconciliation, GSTIN/PAN registry cross-check, supplier confirmation and computation audit.</li>
                    <li>Vinci may call for further documents at any stage. Unsupported, inconsistent or misstated particulars result in forfeiture of the claim and may be reported.</li>
                    <li>Identity is established by one-time password to the registered email; the form is signed by the authorised signatory only.</li>
                  </ol>
                </div>
              )}

              {cur.computed && (
                <div className="mt-6 border border-[#8a6d1f]/40 bg-[#fff9e8] p-5 text-[14px]" data-testid="computation-box">
                  <div className="font-mono text-[12px] uppercase tracking-[0.25em] text-[#8a6d1f]">Vinci Trade Desk computation (locked)</div>
                  <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mt-3">
                    <dt className="text-[#555]">Order value</dt><dd className="font-mono">{inr(meta.order_value)}</dd>
                    <dt className="text-[#555]">Base commission</dt><dd className="font-mono">{meta.base_rate_pct}%</dd>
                    <dt className="text-[#555]">Eligible commission</dt><dd className="font-mono">{inr(meta.eligible_commission_inr)}</dd>
                    <dt className="text-[#555]">Fulfilment → applying gap</dt><dd className="font-mono">{meta.days_gap} days · {meta.slab_label}</dd>
                    <dt className="text-[#555]">Slab payout</dt><dd className="font-mono">{meta.slab_payout_pct}% · deduction {inr(meta.deduction_inr)}</dd>
                    <dt className="text-[#555] font-semibold">Payable commission</dt><dd className="font-mono font-semibold">{inr(meta.payable_commission_inr)}</dd>
                  </dl>
                </div>
              )}

              <div className="mt-4">
                {cur.fields.map((f, i) => <Field key={f.key} page={page} f={f} idx={i} value={pageAnswers[f.key]} onChange={(v) => setA(f.key, v)} />)}
              </div>

              {cur.upload && (
                <div className="mt-6 space-y-3" data-testid="upload-section">
                  {!session && <div className="border border-[#a3261f]/40 bg-[#fdeeec] p-4 text-[14px] text-[#7a1c16]">Uploads require identity verification. Request your OTP below, then return to this page.</div>}
                  {DOC_TYPES.map((d) => {
                    const have = docs.filter((x) => x.doc_type === d.key);
                    return (
                      <div key={d.key} className="flex flex-wrap items-center justify-between gap-3 border border-[#c9bfa3] bg-[#faf8f2] px-4 py-3" data-testid={`doc-${d.key}`}>
                        <div className="text-[14px]"><FileText size={14} className="inline mr-2 text-[#8a6d1f]" />{d.label}{d.required && <span className="text-[#a3261f]"> *</span>}
                          {have.length > 0 && <div className="text-[12px] text-[#2a6b3a] mt-1">{have.map((h) => h.filename).join(" · ")}</div>}</div>
                        <label className={`inline-flex items-center gap-2 px-3 py-2 text-[13px] font-semibold uppercase tracking-wide border ${session ? "border-[#111] cursor-pointer hover:bg-[#111] hover:text-[#f3efe4]" : "border-[#999] text-[#999]"}`}>
                          <Upload size={14} /> Upload<input type="file" data-testid={`upload-${d.key}`} className="hidden" disabled={!session || busy} accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => upload(d.key, e.target.files?.[0])} />
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}

              {(cur.upload || cur.final) && !session && (
                <div className="mt-8 border-2 border-[#111] bg-[#fff] p-6" data-testid="otp-box">
                  <div className="font-mono text-[12px] uppercase tracking-[0.3em] text-[#8a6d1f]">Identity verification · Vinci email OTP</div>
                  <p className="text-[14px] text-[#444] mt-2">A one-time password will be sent to the claimant email on record (<span className="font-mono">{meta.buyer_email}</span>).</p>
                  <div className="mt-4 flex flex-wrap gap-3 items-center">
                    <button data-testid="otp-send" disabled={busy} onClick={startOtp} className="px-4 py-2.5 bg-[#111] text-[#f3efe4] text-[13px] font-semibold uppercase tracking-wide disabled:opacity-50">{otpSent ? "Resend OTP" : "Send OTP"}</button>
                    {otpSent && <>
                      <input data-testid="otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="border border-[#2a2a2a] bg-[#faf8f2] px-3 py-2.5 font-mono text-[16px] tracking-[0.4em] w-44" />
                      <button data-testid="otp-verify" disabled={busy || otp.length < 4} onClick={verifyOtp} className="px-4 py-2.5 text-[13px] font-semibold uppercase tracking-wide disabled:opacity-50" style={{ background: GOLD }}>Verify</button>
                      <span className="text-[13px] text-[#555]">Sent to {otpSent.sent_to} · valid {otpSent.ttl_minutes} min</span>
                    </>}
                  </div>
                </div>
              )}

              {cur.final && (
                <div className="mt-8" data-testid="final-declarations">
                  <div className="font-mono text-[12px] uppercase tracking-[0.3em] text-[#8a6d1f]">Evidence register · documents on record</div>
                  <ul className="mt-2 mb-6 border border-[#c9bfa3] bg-[#faf8f2] divide-y divide-dotted divide-[#bbb] text-[13px]" data-testid="final-evidence-register">
                    {DOC_TYPES.map((d) => { const n = docs.filter((x) => x.doc_type === d.key).length; const ok = n > 0; return (
                      <li key={d.key} className="flex items-center justify-between px-4 py-2" data-testid={`final-evidence-${d.key}`} data-state={ok ? "on_record" : d.required ? "not_furnished" : "optional"}>
                        <span>{d.label}{d.required && <span className="text-[#a3261f]"> *</span>}{n > 1 && <span className="font-mono text-[11px] text-[#777] ml-2">×{n}</span>}</span>
                        <span className={`font-mono text-[10px] tracking-[0.2em] ${ok ? "text-[#1d7a5f]" : d.required ? "text-[#a3261f]" : "text-[#888]"}`}>{ok ? "ON RECORD" : d.required ? "NOT FURNISHED" : "OPTIONAL"}</span>
                      </li>); })}
                  </ul>
                  {docsMissing.length > 0 && <div className="mb-6 border border-[#a3261f]/40 bg-[#fdeeec] p-4 text-[14px] text-[#7a1c16]" data-testid="final-evidence-missing">Submission is barred until mandatory evidence is on record: {docsMissing.map((d) => d.label).join(", ")}. Return to Part 9 to furnish it.</div>}
                  <div className="font-mono text-[12px] uppercase tracking-[0.3em] text-[#8a6d1f]">Final declarations</div>
                  {DECLARATIONS.map((d) => (
                    <label key={d.key} className="flex items-start gap-3 py-2.5 border-b border-dotted border-[#bbb] text-[14px] cursor-pointer">
                      <input type="checkbox" data-testid={`decl-${d.key}`} checked={!!decl[d.key]} onChange={(e) => setDecl((x) => ({ ...x, [d.key]: e.target.checked }))} className="mt-1 accent-[#8a6d1f] w-4 h-4" /> {d.label}
                    </label>
                  ))}
                  <div className="mt-6 grid sm:grid-cols-2 gap-6 text-[13px]">
                    <div className="border-t-2 border-[#111] pt-2">Signature of claimant<div className="font-serif italic text-xl mt-2">{(answers.page_11 || {}).signature_name || "\u00a0"}</div></div>
                    <div className="border-t-2 border-[#111] pt-2">Date<div className="font-mono mt-2">{new Date().toISOString().slice(0, 10)}</div></div>
                  </div>
                </div>
              )}

              <div className="mt-10 flex items-center justify-between border-t-2 border-[#111] pt-5">
                <button data-testid="page-prev" disabled={page === 1} onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0 }); }} className="inline-flex items-center gap-1 px-4 py-2.5 border border-[#111] text-[13px] font-semibold uppercase tracking-wide disabled:opacity-30"><ChevronLeft size={14} /> Previous</button>
                <div className="text-[12px] font-mono text-[#555]">Page {page} / 11</div>
                {page < 11 ? (
                  <button data-testid="page-next" onClick={next} className="inline-flex items-center gap-1 px-5 py-2.5 bg-[#111] text-[#f3efe4] text-[13px] font-semibold uppercase tracking-wide">Next <ChevronRight size={14} /></button>
                ) : (
                  <button data-testid="claim-submit" disabled={!session || busy || docsMissing.length > 0} onClick={submit} title={session ? (docsMissing.length ? "Mandatory evidence not on record" : "") : "Verify identity to unlock"} className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-bold uppercase tracking-wide disabled:opacity-40" style={{ background: GOLD }}>
                    {session ? <ShieldCheck size={14} /> : <Lock size={14} />} Submit claim form
                  </button>
                )}
              </div>
            </div>
          </section>
          <p className="text-center text-[12px] text-[#777] mt-6">Vinci Incorporation, Powai, Mumbai · This form is an official record. Furnishing false information is punishable under applicable law.</p>
        </main>
      )}
    </div>
  );
}
